// src/views/bots/flow/components/BotFlowHeader.tsx
// Compact header for bot flow builder with name, active toggle, description

import React, { useState } from 'react';
import styles from './BotFlowHeader.module.css';
import logger from '../../../../utils/logger';
import Tooltip from '../../../../components/Tooltip/Tooltip';

interface BotFlowHeaderProps {
  name: string;
  description: string;
  isActive: boolean;
  isExistingLeadBot: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onActiveChange: (isActive: boolean) => void;
  onExistingLeadBotChange: (isExistingLeadBot: boolean) => void;
  onBack: () => void;
  onSave: () => void;
  onAnalytics?: () => void; // Optional - only in edit mode
  isSaving?: boolean;
  isValid?: boolean;
  isEdit?: boolean;
  // Auto-save status
  isAutoSaving?: boolean;
  lastAutoSaved?: Date | null;
  autoSaveError?: string | null;
}

const BotFlowHeader: React.FC<BotFlowHeaderProps> = ({
  name,
  description,
  isActive,
  isExistingLeadBot,
  onNameChange,
  onDescriptionChange,
  onActiveChange,
  onExistingLeadBotChange,
  onBack,
  onSave,
  onAnalytics,
  isSaving = false,
  isValid = true,
  isEdit = false,
  isAutoSaving = false,
  lastAutoSaved = null,
  autoSaveError = null,
}) => {
  const [showDescription, setShowDescription] = useState(false);

  // Format relative time for auto-save display
  const formatAutoSaveTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 5) return 'just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;

    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Build tooltip for disabled save button
  const getSaveTooltip = (): string => {
    if (isSaving) return 'Saving...';
    if (!name.trim()) return 'Enter a bot name to save';
    if (!isValid) return 'Add trigger keywords to the Start node to save';
    return isEdit ? 'Update this bot' : 'Save this bot';
  };

  logger.log('[BotFlowHeader] Rendered', { name, isActive, isEdit });

  return (
    <div className={styles.header} data-tour="bot-header">
      {/* Top Row: Name, Active, Actions */}
      <div className={styles.topRow}>
        {/* Left: Back + Bot Name + Active Toggle */}
        <div className={styles.leftSection}>
          <Tooltip content="Return to bot list" position="bottom">
            <button className={styles.backButton} onClick={onBack}>
              ←
            </button>
          </Tooltip>

          <input
            type="text"
            className={styles.nameInput}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Bot Name"
            maxLength={100}
          />

          <Tooltip content="When active, this bot replies to incoming WhatsApp messages matching its keywords" position="bottom">
            <label className={styles.activeToggle}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => onActiveChange(e.target.checked)}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleLabel}>
                {isActive ? '✅ Active' : '❌ Inactive'}
              </span>
            </label>
          </Tooltip>

          <Tooltip content="When enabled, the bot also responds to existing leads — not just new incoming ones" position="bottom">
            <label className={styles.activeToggle}>
              <input
                type="checkbox"
                checked={isExistingLeadBot}
                onChange={(e) => onExistingLeadBotChange(e.target.checked)}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleLabel}>
                {isExistingLeadBot ? '👥 All Leads' : '📥 New Leads Only'}
              </span>
            </label>
          </Tooltip>

          <button
            className={styles.descButton}
            onClick={() => setShowDescription(!showDescription)}
            title={showDescription ? 'Hide description' : 'Add description'}
          >
            {showDescription ? '📝 Hide' : '📝 Description'}
          </button>
        </div>

        {/* Right: Actions */}
        <div className={styles.rightSection}>
          {/* Auto-save Status (Edit mode only) */}
          {isEdit && (
            <div className={styles.autoSaveStatus}>
              {isAutoSaving ? (
                <span className={styles.autoSaving}>
                  <span className={styles.savingDot}></span>
                  Saving...
                </span>
              ) : autoSaveError ? (
                <span className={styles.autoSaveError}>
                  ⚠️ {autoSaveError}
                </span>
              ) : lastAutoSaved ? (
                <span className={styles.autoSaved}>
                  ✓ Auto saved {formatAutoSaveTime(lastAutoSaved)}
                </span>
              ) : null}
            </div>
          )}

          {/* Analytics Button (Edit mode only) */}
          {isEdit && onAnalytics && (
            <button className={styles.analyticsButton} onClick={onAnalytics}>
              📊 Analytics
            </button>
          )}

          {/* Save Button */}
          <Tooltip content={getSaveTooltip()} position="bottom">
            <button
              className={styles.saveButton}
              onClick={onSave}
              disabled={!isValid || isSaving}
              data-tour="save-btn"
            >
              {isSaving ? '⏳ Saving...' : `💾 ${isEdit ? 'Update' : 'Save'} Bot`}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Description Row (Collapsible) */}
      {showDescription && (
        <div className={styles.descriptionRow}>
          <textarea
            className={styles.descriptionInput}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Add an optional description to help you remember what this bot does..."
            rows={2}
            maxLength={500}
          />
          <span className={styles.charCount}>
            {description.length}/500
          </span>
        </div>
      )}
    </div>
  );
};

export default BotFlowHeader;
